import type {
  ComposerHistoryCursor,
  ComposerHistoryResponse,
  ConverseRequest,
  RouterEvent,
} from "@bash/shared";
import { destinationOfPath } from "@bash/shared";
import { SERVER_URL } from "@/lib/api/base";
import { getOptionalAccessToken } from "@/lib/api/client";
import { isDesktopShell } from "@/lib/desktop-shell";

export function parseSseChunk(buffer: string): {
  events: RouterEvent[];
  rest: string;
} {
  const events: RouterEvent[] = [];
  const parts = buffer.split("\n\n");
  const rest = parts.pop() ?? "";
  for (const part of parts) {
    const line = part.split("\n").find((l) => l.startsWith("data: "));
    if (!line) continue;
    try {
      events.push(JSON.parse(line.slice(6)) as RouterEvent);
    } catch {
      /* skip malformed */
    }
  }
  return { events, rest };
}

// Situation stamp: tz (IANA) + the in-app view, merged onto whatever context the
// caller already set (e.g. activeTaskId). The view is the DESTINATION the path is
// on (the composer knows the app, spec 2026-09-17 §5.3: support, billing, the
// Brain and the editors stamp their own ids; the query never decides), or nothing
// on a page the registry does not know. Best-effort only, a missing `window`
// (SSR) or a throwing Intl call just drops the field, never throws out of the
// client. The notch's Rust caller stamps nothing by design (spec §4.2): the
// server's per-user last-seen-tz memory covers it after the user's first
// web/in-app turn.
function situationContext(
  existing?: ConverseRequest["context"]
): ConverseRequest["context"] {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* fact drops */
  }
  const view =
    typeof window !== "undefined"
      ? destinationOfPath(window.location.pathname)?.id
      : undefined;
  return { ...existing, ...(tz ? { tz } : {}), ...(view ? { view } : {}) };
}

export async function* converseStream(
  req: ConverseRequest,
  opts?: { signal?: AbortSignal; stream?: boolean }
): AsyncGenerator<RouterEvent> {
  const token = await getOptionalAccessToken();
  if (!token) {
    yield { type: "error", code: "unauthorized", message: "not signed in" };
    return;
  }

  // Host fact, stamped ONCE for every converse surface this client serves
  // (composer, ⌘K): the surface id alone can't tell the desktop shell's
  // webview from a plain browser tab, and placement routes machine work on
  // exactly this fact (a desktop-composer browser ask dispatched to the cloud
  // without it — founder-reported 2026-07-16). The same fact the classic
  // USER_MESSAGE lane stamps as surface:'native'.
  const request: ConverseRequest = {
    ...req,
    host: isDesktopShell() ? "native" : "web",
    context: situationContext(req.context),
  };

  let res: Response;
  try {
    res = await fetch(`${SERVER_URL}/api/composer/converse`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
      signal: opts?.signal,
    });
  } catch (err) {
    yield {
      type: "error",
      code: "internal",
      message: `request failed: ${
        err instanceof Error ? err.message : String(err)
      }`,
    };
    return;
  }
  if (!res.ok) {
    yield {
      type: "error",
      code: "internal",
      message: `converse ${res.status}`,
    };
    return;
  }

  // Streaming path: a real browser reads the SSE body incrementally, so inline
  // answers arrive token-by-token. Opt-in (`stream: true`) — the voice-card
  // WKWebView keeps the buffered path below (its cross-origin event-stream reader
  // can throw mid-stream). Reuses parseSseChunk's rolling-buffer contract: feed the
  // running buffer, emit every complete frame, carry `rest` forward.
  if (opts?.stream && res.body) {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const { events, rest } = parseSseChunk(buf);
        buf = rest;
        for (const e of events) yield e;
      }
      const { events } = parseSseChunk(
        buf.endsWith("\n\n") ? buf : `${buf}\n\n`
      );
      for (const e of events) yield e;
    } catch (err) {
      yield {
        type: "error",
        code: "internal",
        message: `read failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      };
    }
    return;
  }

  // Buffer the WHOLE SSE body, then parse every frame at once — do NOT read res.body
  // incrementally. The voice-card overlay runs in a WKWebView, which does not reliably
  // expose a cross-origin text/event-stream response as an incrementally-readable stream:
  // res.body.getReader().read() can throw mid-stream there (the failure that surfaced as
  // an opaque "Something went wrong"). res.text() reads the full body and is robust
  // everywhere. Trade-off: the inline answer no longer streams token-by-token (it appears
  // once, when the turn completes) — acceptable for v1. Restore incremental reads behind a
  // capability check if a streaming-capable surface (e.g. the web composer) adopts this.
  let body: string;
  try {
    body = await res.text();
  } catch (err) {
    yield {
      type: "error",
      code: "internal",
      message: `read failed: ${
        err instanceof Error ? err.message : String(err)
      }`,
    };
    return;
  }
  const { events } = parseSseChunk(
    body.endsWith("\n\n") ? body : `${body}\n\n`
  );
  for (const e of events) yield e;
}

/** One page of the account transcript. `failed` when it could not be read
 *  (signed out, a non-OK answer, a network error): no entries, no cursor. */
export interface ComposerHistoryPage extends ComposerHistoryResponse {
  failed?: boolean;
}

const failedPage = (): ComposerHistoryPage => ({ entries: [], failed: true });

/**
 * Fetch one page of the account-synced composer history
 * (GET /api/composer/history), newest first: the newest page without a
 * cursor, the next older one with the previous page's `nextCursor`. Never
 * throws: a failure is a `failed` page, and the history card falls back to
 * its localStorage mirror. An old server sends no `nextCursor`, so no older
 * page is ever asked for.
 */
export async function fetchComposerHistory(
  cursor?: ComposerHistoryCursor
): Promise<ComposerHistoryPage> {
  try {
    const token = await getOptionalAccessToken();
    if (!token) return failedPage();
    // The cursor goes back verbatim: `before` is Postgres's own instant
    // (microseconds, '+00:00'), and URLSearchParams encodes its '+', which a
    // raw query string would turn into a space (the server answers 400).
    const query = cursor
      ? `?${new URLSearchParams({ before: cursor.before, beforeId: cursor.beforeId })}`
      : "";
    const res = await fetch(`${SERVER_URL}/api/composer/history${query}`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) return failedPage();
    const body = (await res.json()) as ComposerHistoryResponse;
    if (!Array.isArray(body.entries)) return failedPage();
    return body.nextCursor
      ? { entries: body.entries, nextCursor: body.nextCursor }
      : { entries: body.entries };
  } catch {
    return failedPage();
  }
}
